import { useState, useEffect, useMemo, useRef, type CSSProperties, type ReactNode } from 'react';
import { useFrozenColumns } from '../hooks/useFrozenColumns';
import { useFitHeight } from '../hooks/useFitHeight';
import { useColumnFilters, clarityCompare, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import {
  collection, doc, getDoc, getDocs, onSnapshot, query, where, orderBy, serverTimestamp, runTransaction,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import ReturnModal from '../components/ReturnModal';
import { Button, Modal, ModalBanner, FormField, Dropdown, EntityCombobox } from '../components/ui';
import { useSettings } from '../hooks/useSettings';
import { canAccessPage } from '../utils/pages';
import { useSalePopup } from '../components/SalePopup';
import { useCategoryFilter } from '../context/CategoryFilterContext';
import { fmtMoney } from '../utils/currency';
import { downloadCSV } from '../utils/csv';
import type {
  InventoryItem, InventoryStatus, Entity, PurchaseInvoice, PurchaseLineItem, CodeHistoryEntry, SaleInvoice, SaleLineItem, ItemCode,
  StockRevaluation,
} from '../types';

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

type SortKey = 'sku' | 'code' | 'shape' | 'carat' | 'color' | 'clarity' | 'lab'
  | 'size' | 'subSize' | 'grading' | 'fluorescence' | 'measurements' | 'costPerCt' | 'pricePerCt' | 'status';

const STATUS_RANK: Record<InventoryStatus, number> = { 'in-stock': 0, 'on-memo': 1, 'returned': 2, 'sold': 3, 'merged': 4, 'written-off': 5 };

// ── Customize Columns ────────────────────────────────────────────────────────
// Personal, per-browser preference (localStorage) — deliberately NOT a
// BusinessSettings/Firestore field, since column layout shouldn't sync
// across every user of the business. Actions is excluded on purpose: it's
// a controls column, not data, and always renders.

type ColumnKey = 'code' | 'sku' | 'shape' | 'carat' | 'color' | 'clarity'
  | 'lab' | 'size' | 'subSize' | 'grading' | 'fluorescence' | 'measurements' | 'costPerCt' | 'pricePerCt' | 'status';

interface ColumnDef { key: ColumnKey; label: string; sortKey: SortKey; skeletonWidth: number }

// Order here = column order in the table. Cut/Polish/Symmetry, Fluorescence,
// and Measurements are certified-only (same as Lab/Cert No and Size/Sub Size
// are loose-only) — each cell shows a muted "—" for the other type, matching
// the existing lab/size pattern rather than hiding whole columns per type.
const COLUMNS: ColumnDef[] = [
  { key: 'code',         label: 'Code',          sortKey: 'code',         skeletonWidth: 80 },
  { key: 'sku',          label: 'SKU',           sortKey: 'sku',          skeletonWidth: 80 },
  { key: 'shape',        label: 'Shape',         sortKey: 'shape',        skeletonWidth: 80 },
  { key: 'carat',        label: 'Carat',         sortKey: 'carat',        skeletonWidth: 55 },
  { key: 'color',        label: 'Color',         sortKey: 'color',        skeletonWidth: 55 },
  { key: 'clarity',      label: 'Clarity',       sortKey: 'clarity',      skeletonWidth: 60 },
  { key: 'lab',          label: 'Lab / Cert No', sortKey: 'lab',          skeletonWidth: 110 },
  { key: 'size',         label: 'Size',          sortKey: 'size',         skeletonWidth: 55 },
  { key: 'subSize',      label: 'Sub Size',      sortKey: 'subSize',      skeletonWidth: 55 },
  { key: 'grading',      label: 'Cut/Pol/Sym',   sortKey: 'grading',      skeletonWidth: 110 },
  { key: 'fluorescence', label: 'Fluor.',        sortKey: 'fluorescence', skeletonWidth: 70 },
  { key: 'measurements', label: 'Measurements',  sortKey: 'measurements', skeletonWidth: 100 },
  // Cost/ct = what we paid (costUSD). Price/ct = Cost/ct plus Added Value %
  // (costingUSD), the figure to sell at or above.
  { key: 'costPerCt',    label: 'Cost/ct',       sortKey: 'costPerCt',    skeletonWidth: 80 },
  { key: 'pricePerCt',   label: 'Price/ct',      sortKey: 'pricePerCt',   skeletonWidth: 80 },
  { key: 'status',       label: 'Status',        sortKey: 'status',       skeletonWidth: 90 },
];

const COLUMN_VISIBILITY_KEY = 'inventory-visible-columns';
const DEFAULT_VISIBLE: Record<ColumnKey, boolean> =
  Object.fromEntries(COLUMNS.map(c => [c.key, true])) as Record<ColumnKey, boolean>;

// Merges the saved blob over the all-visible default, key-by-key, so a
// column removed from COLUMNS in a future change is silently dropped and a
// column newly added to COLUMNS defaults to visible for existing users —
// never trust the raw parsed object wholesale.
function loadVisibleColumns(): Record<ColumnKey, boolean> {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (!raw) return { ...DEFAULT_VISIBLE };
    const saved = JSON.parse(raw) as Partial<Record<ColumnKey, boolean>>;
    const merged = { ...DEFAULT_VISIBLE };
    for (const c of COLUMNS) if (typeof saved[c.key] === 'boolean') merged[c.key] = saved[c.key]!;
    return merged;
  } catch { return { ...DEFAULT_VISIBLE }; }
}

function todayISO() { return new Date().toISOString().split('T')[0]; }
// Purchase No is stored in full, but shown here as just the prefix + last 4
// digits to keep the column compact. Keeping the prefix matters — a bare
// last-4 truncation collapses "PC-2026-0001" and "PL-2026-0001" into the
// same "0001", making certified and loose lots indistinguishable.
function purchaseNoDisplay(purchaseNo: string) {
  if (!purchaseNo) return '—';
  const m = purchaseNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  if (m) return `${m[1]}-${m[2].slice(-4)}`;
  // Not a real Lot No format (today this is only ever the shared Dummy/Books
  // Adjustment item's literal "DUMMY") — show it in full rather than an
  // arbitrary last-4 slice, which turned "DUMMY" into "UMMY".
  return purchaseNo;
}
function dueDateDisplay(dateStr: string) {
  const due   = new Date(dateStr + 'T00:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff  = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  return {
    isOverdue: diff < 0,
    badge:     diff === 0 ? 'Today' : diff > 0 ? `${diff}d left` : `${Math.abs(diff)}d over`,
  };
}
function round2(n: number) { return Math.round(n * 100) / 100; }
// Short grade codes for the compact Cut/Pol/Sym list column — matches
// standard cert shorthand (Purchase's line-item form still spells the full
// grade out in its dropdown; this is a display-only abbreviation).
const GRADE_ABBR: Record<string, string> = {
  Excellent: 'EX', 'Very Good': 'VG', Good: 'G', Fair: 'F', Poor: 'P',
};
function gradeAbbr(v?: string): string {
  if (!v) return '—';
  return GRADE_ABBR[v] ?? v;
}
// A GIA, IGI or HRD cert number renders as a link straight to that lab's public
// report check for that report; any other lab (or a missing number) stays
// plain text. Muted em dash for a missing value, same as every other cell.
const CERT_LINKS: Record<string, { url: (no: string) => string; title: string }> = {
  GIA: { url: no => `https://www.gia.edu/report-check?locale=en_US&reportno=${encodeURIComponent(no)}`, title: 'Verify on GIA Report Check' },
  IGI: { url: no => `https://www.igi.org/verify-your-report-sku/?r=${encodeURIComponent(no)}`, title: 'Verify on IGI' },
  // HRD's page reads ?record_number= and runs its own report search on load.
  HRD: { url: no => `https://my.hrdantwerp.com/?record_number=${encodeURIComponent(no)}`, title: 'Verify on HRD Antwerp' },
};
function CertNo({ lab, certificateNo, style }: {
  lab?: string; certificateNo?: string | null; style?: CSSProperties;
}) {
  if (!certificateNo) return <span style={{ opacity: 0.4, ...style }}>—</span>;
  const link = CERT_LINKS[(lab ?? '').trim().toUpperCase()];
  if (!link) return <span style={style}>{certificateNo}</span>;
  return (
    <a
      href={link.url(certificateNo.trim())}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      title={link.title}
      style={{ color: 'var(--primary)', textDecoration: 'underline', ...style }}
    >
      {certificateNo}
    </a>
  );
}
// Same normalization as Purchase.tsx/Sales.tsx/InventoryMerge.tsx — keys the
// shared itemCodes/{codeId}/history ledger.
function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}
// carat - whatever's actively held by concurrent memos (see InventoryMemo in
// types/index.ts). This, not the item's own status, is what gates whether
// there's anything left to memo/sell/merge — an item stays 'in-stock' even
// while part of it is out with a client.
function availableCarat(item: InventoryItem): number {
  return round2(Math.max(0, item.carat - (item.memoCommittedCarat ?? 0)));
}
// What's physically here: carat minus what's out on memo with customers.
// The stock list and its totals count this; the memo part shows under it.
// (Unlike availableCarat, no floor at 0, so the Dummy code can go negative.)
function memoCt(item: InventoryItem): number {
  return item.status === 'in-stock' ? Math.max(0, item.memoCommittedCarat ?? 0) : 0;
}
function inHandCarat(item: InventoryItem): number {
  return round2(item.carat - memoCt(item));
}
/** What we paid for the part in hand. */
function inHandCost(item: InventoryItem): number {
  const m = memoCt(item);
  if (m <= 1e-9 || Math.abs(item.carat) < 1e-9) return item.costUSD ?? 0;
  return round2((item.costUSD ?? 0) * (inHandCarat(item) / item.carat));
}
const perCt = (v: number | undefined, ct: number) => Math.abs(ct) > 1e-9 ? (v ?? 0) / ct : 0;
/** Price (Cost plus Added Value %) of the part in hand. */
function inHandCosting(item: InventoryItem): number {
  const m = memoCt(item);
  if (m <= 1e-9 || Math.abs(item.carat) < 1e-9) return item.costingUSD ?? 0;
  return round2((item.costingUSD ?? 0) * (inHandCarat(item) / item.carat));
}
function stripUndef<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(v => stripUndef(v)) as unknown as T;
  if (obj !== null && typeof obj === 'object' && (obj as object).constructor === Object) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndef(v)])
    ) as T;
  }
  return obj;
}
// ── Icons ─────────────────────────────────────────────────────────────────────

function IconSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.4" />
      <path d="M9.5 9.5l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function IconEmptyDiamond() {
  return (
    <svg width="36" height="36" viewBox="0 0 16 16" fill="none">
      <polygon points="4,2 12,2 14.5,6 8,14.5 1.5,6" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M8 14.5V6M1.5 6h13M4 2l4 4M12 2l-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconColumns() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="2" width="3.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <rect x="5.9" y="2" width="3.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <rect x="10.3" y="2" width="2.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}
function IconExport() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1v8M4 6.5L7 9.5l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.5 10.5v1.5a1 1 0 001 1h9a1 1 0 001-1v-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
// Send On Memo / Memo In moved into the Item History modal (below its
// header) — the Actions column/3-dot menu that used to hold them is gone;
// clicking a row already opens History, so their home is there now instead
// of a second click through a dropdown.
function IconSortArrow({ dir }: { dir: 'asc' | 'desc' }) {
  return <svg width="9" height="9" viewBox="0 0 10 10" fill="none"
    style={{ transform: dir === 'asc' ? 'rotate(180deg)' : undefined, transition: 'transform 0.12s' }}>
    <path d="M2 3.5L5 7l3-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

// ── Sortable column header — matches Purchase's list table ────────────────────

function SortableTh({ label, sortKey, activeKey, dir, onSort, align, minWidth, width, filter }: {
  label: string; sortKey: SortKey; activeKey: SortKey; dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void; align?: 'right' | 'center'; minWidth?: number;
  /** A fixed width (frozen and name columns): the column never resizes. */
  width?: number;
  /** Excel-style filter button for this column (ColumnFilterButton). */
  filter?: ReactNode;
}) {
  const active = activeKey === sortKey;
  return (
    <th
      className={`th-sortable${active ? ' active' : ''}`}
      style={{ textAlign: align, minWidth: width ?? minWidth, width, maxWidth: width }}
      onClick={() => onSort(sortKey)}
    >
      <span className="th-sort-inner" style={{ justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start' }}>
        {label}
        <span className="th-sort-arrow"><IconSortArrow dir={active ? dir : 'desc'} /></span>
        {filter}
      </span>
    </th>
  );
}

// ── Badges ────────────────────────────────────────────────────────────────────

const STATUS_MAP: Record<InventoryStatus, { bg: string; color: string; border: string; label: string }> = {
  'in-stock': { bg: 'var(--success-bg)', color: 'var(--success)', border: 'var(--success)', label: 'In Stock' },
  'on-memo':  { bg: '#fff7ed', color: '#c2410c', border: '#fdba74', label: 'On Memo'  },
  'sold':     { bg: '#eef2ff', color: '#4338ca', border: '#a5b4fc', label: 'Sold'     },
  'returned':    { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1', label: 'Returned'   },
  'merged':      { bg: '#f5f3ff', color: '#6d28d9', border: '#c4b5fd', label: 'Merged'     },
  'written-off': { bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5', label: 'Written Off' },
};

function StatusBadge({ status }: { status: InventoryStatus }) {
  const s = STATUS_MAP[status];
  return <span className="badge" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

// ── Pagination ────────────────────────────────────────────────────────────────

function Pagination({ current, total, totalItems, pageSize, onChange }: {
  current: number; total: number; totalItems: number; pageSize: number; onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (current - 1) * pageSize + 1;
  const to   = Math.min(current * pageSize, totalItems);
  const pages: (number | 'e')[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3) pages.push('e');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('e');
    pages.push(total);
  }
  return (
    <div className="pagination">
      <span className="pagination-info">
        Showing <strong>{from}–{to}</strong> of <strong>{totalItems}</strong> item{totalItems !== 1 ? 's' : ''}
      </span>
      <div className="pagination-btns">
        <button className="pg-btn" disabled={current === 1} onClick={() => onChange(current - 1)}>‹</button>
        {pages.map((p, i) => p === 'e'
          ? <span key={`e${i}`} style={{ minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-3)' }}>…</span>
          : <button key={p} className={`pg-btn${p === current ? ' active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )}
        <button className="pg-btn" disabled={current === total} onClick={() => onChange(current + 1)}>›</button>
      </div>
    </div>
  );
}

// ── Memo Out (Send On Memo) modal ────────────────────────────────────────────

// Memo Out — never splits the item and never touches its status. Only the
// carat entered gets "held": a new InventoryMemo doc is written to
// inventory/{id}/memos, and the item's cached memoCommittedCarat goes up by
// that much. The item's own `carat` and `status` ('in-stock') never change,
// so whatever's still available keeps circulating — sellable, mergeable, or
// memo-able to a DIFFERENT client — while this memo's carat is held. A
// itemCodes/{code}/history entry still records the event so it shows up in
// that Code's lifecycle ledger alongside memo-in and sale events.
function MemoModal({ open, onClose, item, customers, residualCostPerCt }: {
  open: boolean; onClose: () => void; item: InventoryItem | null; customers: Entity[];
  /** The Item History popup's Residual Cost/ct, shown again here. */
  residualCostPerCt?: number;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const [entityId,    setEntityId]    = useState('');
  const [carat,       setCarat]       = useState('');
  const [pricePerCt,  setPricePerCt]  = useState('');
  const [memoDate,    setMemoDate]    = useState(todayISO());
  const [returnDate,  setReturnDate]  = useState('');
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');

  const avail = item ? availableCarat(item) : 0;
  // The stone's Price/ct (paid plus Added Value %). The memo's Price/ct
  // starts there and can be changed.
  const listPrice  = item ? perCt(item.costingUSD, item.carat) : 0;
  const autoPrice  = listPrice > 0 ? listPrice.toFixed(2) : '';

  useEffect(() => {
    if (open && item) {
      setEntityId(''); setCarat(String(availableCarat(item))); setPricePerCt(autoPrice);
      setMemoDate(todayISO()); setReturnDate(''); setError('');
    }
  }, [open, item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const caratNum = parseFloat(carat) || 0;
  const priceNum = parseFloat(pricePerCt) || 0;
  const amountUSD = round2(caratNum * priceNum);

  async function handleSave() {
    if (!item) return;
    if (!entityId) { setError('Select a customer.'); return; }
    if (!caratNum || caratNum <= 0) { setError('Enter a valid carat.'); return; }
    if (caratNum > avail + 1e-6) { setError(`Only ${avail.toFixed(2)}ct available.`); return; }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Database not connected.'); setSaving(false); return; }

    try {
      await runTransaction(db, async tx => {
        const ref = doc(db, 'inventory', item.id);
        const snap = await tx.get(ref);
        if (!snap.exists() || (snap.data() as InventoryItem).status !== 'in-stock') {
          throw new Error('This item changed status. Please reload and try again.');
        }
        const current = snap.data() as InventoryItem;
        const currentAvail = availableCarat(current);
        if (caratNum > currentAvail + 1e-6) {
          throw new Error(`Only ${currentAvail.toFixed(2)}ct available now — please reload and try again.`);
        }

        const memoRef = doc(collection(db, 'inventory', item.id, 'memos'));
        tx.set(memoRef, stripUndef({
          businessId: business.id, inventoryItemId: item.id, entityId,
          shape: current.shape, color: current.color, clarity: current.clarity,
          sku: current.sku, code: current.code,
          carat: caratNum, pricePerCt: priceNum || undefined, amountUSD: amountUSD || undefined,
          date: memoDate, returnByDate: returnDate || undefined,
          status: 'active' as const,
          createdAt: serverTimestamp(), createdBy: user!.id,
        }));

        tx.update(ref, {
          memoCommittedCarat: (current.memoCommittedCarat ?? 0) + caratNum,
          updatedAt: serverTimestamp(),
        });

        if (current.code) {
          tx.set(doc(collection(db, 'itemCodes', normalizeCode(current.code), 'history')), stripUndef({
            type: 'memo-out', date: memoDate, carat: caratNum, entityId,
            pricePerCt: priceNum || undefined, amountUSD: amountUSD || undefined,
            inventoryItemId: item.id, createdAt: serverTimestamp(), createdBy: user!.id,
          }));
        }
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save.');
    }
    setSaving(false);
  }

  return (
    <Modal
      open={open} onClose={onClose} hideHead size="lg"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>Send On Memo</Button>
          </div>
        </div>
      }
    >
      <ModalBanner
        title={item ? `${item.shape} ${[item.color, item.clarity].filter(Boolean).join('/')}` : 'Send On Memo'}
        eyebrow="Send On Memo"
        right={item && (
          <FactPanel>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
                Code
              </div>
              {item.code ? <LotPill>{item.code}</LotPill> : <span style={{ opacity: 0.4 }}>—</span>}
            </div>
            <VDivider />
            <MiniFact label="Cts" value={item.carat.toFixed(2)} />
            {avail < item.carat - 1e-6 && (<>
              <VDivider />
              <MiniFact label="Free Cts" value={avail.toFixed(2)} />
            </>)}
            <VDivider />
            <MiniFact label="Residual Cost/ct" value={money(residualCostPerCt ?? listPrice)} />
            <VDivider />
            <MiniFact label="Price/ct" value={money(listPrice)} />
          </FactPanel>
        )}
        onClose={onClose}
      />

      <FormField label="Customer" required>
        <EntityCombobox
          entities={customers}
          value={entityId}
          onChange={setEntityId}
          placeholder="Select customer…"
        />
      </FormField>
      <div className="form-row-2">
        <FormField label={`Carat (max ${avail.toFixed(2)})`} required>
          <input
            type="number" step="0.01" min="0.01" max={avail} className="no-spinner"
            value={carat} onChange={e => setCarat(e.target.value)}
          />
        </FormField>
        <FormField label="Price/ct">
          <input
            type="number" step="0.01" min="0" className="no-spinner"
            value={pricePerCt} onChange={e => setPricePerCt(e.target.value)}
            placeholder="0.00"
          />
        </FormField>
      </div>

      <div className="form-row-2">
        <FormField label="Memo Date" required>
          <input type="date" value={memoDate} onChange={e => setMemoDate(e.target.value)} />
        </FormField>
        <FormField label="Expected Return Date">
          <input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)} />
        </FormField>
      </div>
    </Modal>
  );
}


// ── Purchase preview modal — opened by clicking a Purchase No ────────────────

// Shared inline table styles for the line-items sub-table, matching the Purchase list's invoice preview.
const TH: CSSProperties = {
  padding: '7px 10px', fontWeight: 600, fontSize: 10.5,
  color: 'var(--text-2)', textAlign: 'left', whiteSpace: 'nowrap',
  background: 'var(--surface-2)', borderBottom: '1.5px solid var(--border)',
};
const TD: CSSProperties = {
  padding: '6px 8px', verticalAlign: 'middle',
  borderBottom: '1px solid var(--surface-2)',
};

function resolveItemType(li: PurchaseLineItem, invoiceType: string): 'certified' | 'loose' {
  if (li.itemType) return li.itemType;
  return invoiceType === 'polished-certified' ? 'certified' : 'loose';
}

function LineItemsSubTable({ items, invoiceType }: { items: PurchaseLineItem[]; invoiceType: string }) {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);
  if (!items || items.length === 0) {
    return <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-3)' }}>No line items.</div>;
  }

  const itemTypes = items.map(li => resolveItemType(li, invoiceType));
  const allCert   = itemTypes.every(t => t === 'certified');
  const allLoose  = itemTypes.every(t => t === 'loose');

  const totalCarat  = items.reduce((s, li) => s + li.carat, 0);
  const totalAmount = items.reduce((s, li) => s + li.amountUSD, 0);
  const avgPricePerCt = totalCarat > 0 ? totalAmount / totalCarat : 0;

  return (
    <div className="line-sub-scroll" style={{ background: 'var(--surface-2)', borderTop: '1px solid var(--border)' }}>
      {/* Scrolls inside its own box (heads and Total pinned), not the whole popup. */}
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 28, textAlign: 'center' }}>#</th>
            <th style={TH}>Code</th>
            {!allLoose  && <th style={TH}>Lab</th>}
            <th style={TH}>Shape</th>
            {!allCert   && <th style={TH}>SKU</th>}
            <th style={TH}>Ct</th>
            <th style={TH}>Color</th>
            <th style={TH}>Clarity</th>
            {!allLoose  && <th style={TH}>Cert No</th>}
            {!allLoose  && <th style={TH}>Rap/ct</th>}
            {!allLoose  && <th style={TH}>List%</th>}
            {!allCert   && <th style={TH}>Size</th>}
            {!allCert   && <th style={TH}>Sub</th>}
            <th style={{ ...TH, textAlign: 'right' }} title="What we paid per ct">Cost/ct</th>
            <th style={{ ...TH, textAlign: 'right' }}>Amount</th>
            <th style={{ ...TH, textAlign: 'right' }} title="Cost/ct plus Added Value %">Price/ct</th>
          </tr>
        </thead>
        <tbody>
          {items.map((li, i) => {
            const liType = itemTypes[i];
            const isCert = liType === 'certified';
            const rowBg  = i % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)';
            return (
              <tr key={li.id || i} style={{ background: rowBg }}>
                <td style={{ ...TD, textAlign: 'center', color: 'var(--text-3)' }}>{i + 1}</td>

                <td style={TD}>{li.code ? <LotPill>{li.code}</LotPill> : <span style={{ opacity: 0.4 }}>—</span>}</td>

                {!allLoose && (isCert
                  ? <td style={TD}><span className="badge badge-primary" style={{ fontSize: 10 }}>{li.lab || '—'}</span></td>
                  : <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                )}

                <td style={TD}>{li.shape}</td>
                {!allCert && (isCert
                  ? <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                  : <td style={TD}>{li.sku ? <LotPill>{li.sku}</LotPill> : <span style={{ opacity: 0.4 }}>—</span>}</td>
                )}
                <td style={{ ...TD, fontFamily: 'var(--font-num)' }}>{li.carat.toFixed(2)}</td>
                <td style={TD}>{li.color}</td>
                <td style={TD}>{li.clarity}</td>

                {!allLoose && (isCert
                  ? <td style={{ ...TD, fontFamily: 'var(--font-num)', fontSize: 11 }}><CertNo lab={li.lab} certificateNo={li.certificateNo} /></td>
                  : <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                )}
                {!allLoose && (isCert
                  ? <td style={{ ...TD, fontFamily: 'var(--font-num)' }}>{li.rapPricePerCt != null ? money(li.rapPricePerCt) : '—'}</td>
                  : <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                )}
                {!allLoose && (isCert
                  ? <td style={{ ...TD, color: (li.priceListPercent ?? 0) < 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                      {li.priceListPercent != null ? `${li.priceListPercent > 0 ? '+' : ''}${li.priceListPercent}%` : '—'}
                    </td>
                  : <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                )}

                {!allCert && (isCert
                  ? <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                  : <td style={{ ...TD, fontSize: 11 }}>{li.size || '—'}</td>
                )}
                {!allCert && (isCert
                  ? <td style={{ ...TD, textAlign: 'center', color: 'var(--border)' }}>—</td>
                  : <td style={{ ...TD, fontSize: 11 }}>{li.subSize || '—'}</td>
                )}

                <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(li.carat > 0 ? li.amountUSD / li.carat : 0)}</td>
                <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(li.amountUSD)}</td>
                <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 11 }}>{money(li.costPerCt)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ background: 'var(--surface)', borderTop: '2px solid var(--border)' }}>
            <td colSpan={1 + 1 + (allLoose ? 0 : 1) + 1 + (allCert ? 0 : 1)} style={{ ...TD, fontWeight: 700, fontSize: 11, color: 'var(--text-2)' }}>
              Total
            </td>
            <td style={{ ...TD, fontFamily: 'var(--font-num)', fontWeight: 700 }}>{totalCarat.toFixed(2)}</td>
            <td style={TD} />
            <td style={TD} />
            {!allLoose && <td style={TD} />}
            {!allLoose && <td style={TD} />}
            {!allLoose && <td style={TD} />}
            {!allCert  && <td style={TD} />}
            {!allCert  && <td style={TD} />}
            <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(avgPricePerCt)}</td>
            <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(totalAmount)}</td>
            <td style={TD} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Inline label|value stat — the app's standard neutral "fact" chip (also used
// The one identifier worth calling out from the rest — same highlighted pill
// used for Lot No everywhere else in the app (the invoice list, Inventory's
// SKU column, etc.), so it's instantly recognizable rather than blending in.
function LotPill({ children }: { children: ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
      fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 5, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}

// Compact label-over-value pair for the header's right-hand fact cluster —
// small enough that several fit on one line next to the title without a box.
function MiniFact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)' }}>
        {value}
      </div>
    </div>
  );
}

// Thin vertical rule between facts inside a FactPanel.
function VDivider() {
  return <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)' }} />;
}

// Groups the header's fact cluster into one floating pill-shaped panel —
// without this, several label/value pairs sitting bare next to the title
// just read as scattered text with no visual relationship to each other.
// Rounded ends + a soft shadow give it its own identity instead of looking
// like just another rectangular box on the page.
function FactPanel({ children }: { children: ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 999, padding: '7px 18px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      {children}
    </div>
  );
}

function PurchasePreviewModal({ open, onClose, invoice, loading, error, entityName }: {
  open: boolean; onClose: () => void; invoice: PurchaseInvoice | null;
  loading: boolean; error: string; entityName: (id?: string) => string;
}) {
  const isPaid = invoice?.paymentStatus === 'paid';
  const dd = invoice ? dueDateDisplay(invoice.dueDate) : null;
  const purchaseDateLabel = invoice?.purchaseDate
    ? new Date(invoice.purchaseDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  return (
    <Modal
      open={open} onClose={onClose}
      hideHead
      size="full"
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      <ModalBanner
        title={invoice ? entityName(invoice.fromEntityId) : 'Purchase Invoice'}
        eyebrow="Purchase Invoice"
        right={invoice && (
          <FactPanel>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
                Lot No
              </div>
              <LotPill>{purchaseNoDisplay(invoice.invoiceNo)}</LotPill>
            </div>
            <VDivider />
            <MiniFact label="Supplier Invoice" value={invoice.supplierInvoiceNo || '—'} />
            <VDivider />
            <MiniFact label="Date" value={purchaseDateLabel} />
          </FactPanel>
        )}
        onClose={onClose}
      />

      {loading && <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Loading…</p>}
      {error && <div className="msg msg-error">{error}</div>}
      {invoice && !loading && (
        <>
          {!isPaid && dd?.isOverdue && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16,
              background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger)',
              borderRadius: 'var(--r-sm)', padding: '6px 12px', fontSize: 12, fontWeight: 700,
            }}>
              {dd.badge}
            </div>
          )}
          {invoice.notes && (
            <p className="form-hint" style={{ marginBottom: 12 }}>Notes: {invoice.notes}</p>
          )}
          <LineItemsSubTable items={invoice.lineItems ?? []} invoiceType={invoice.type} />
        </>
      )}
    </Modal>
  );
}

// Read-only, deliberately lighter than Sales.tsx's own full detail sub-table
// (no broker/description columns) — this is a quick reference opened from
// the Item History table, not the Sales page's own invoice detail view.
function SaleLineItemsMiniTable({ items }: { items: SaleLineItem[] }) {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v, settings.currency);
  if (!items || items.length === 0) {
    return <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text-3)' }}>No line items.</div>;
  }
  return (
    <div className="line-sub-scroll" style={{ background: 'var(--surface-2)', borderTop: '1px solid var(--border)' }}>
      {/* Scrolls inside its own box (heads and Total pinned), not the whole popup. */}
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 28, textAlign: 'center' }}>#</th>
            <th style={TH}>Code</th>
            <th style={TH}>Shape</th>
            <th style={TH}>Color</th>
            <th style={TH}>Clarity</th>
            <th style={{ ...TH, textAlign: 'right' }}>Carat</th>
            <th style={{ ...TH, textAlign: 'right' }}>Price/ct</th>
            <th style={{ ...TH, textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((li, i) => (
            <tr key={li.id || i} style={{ background: i % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)' }}>
              <td style={{ ...TD, textAlign: 'center', color: 'var(--text-3)' }}>{i + 1}</td>
              <td style={TD}>{li.code ? <LotPill>{li.code}</LotPill> : <span style={{ opacity: 0.4 }}>—</span>}</td>
              <td style={TD}>{li.shape}</td>
              <td style={TD}>{li.color}</td>
              <td style={TD}>{li.clarity}</td>
              <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{li.carat.toFixed(2)}</td>
              <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(li.salePricePerCt)}</td>
              <td style={{ ...TD, textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 600 }}>{money(li.amountUSD)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SaleInvoicePreviewModal({ open, onClose, invoice, loading, error, entityName }: {
  open: boolean; onClose: () => void; invoice: SaleInvoice | null;
  loading: boolean; error: string; entityName: (id?: string) => string;
}) {
  const isPaid = invoice?.paymentStatus === 'paid';
  const dd = invoice ? dueDateDisplay(invoice.dueDate) : null;
  const saleDateLabel = invoice?.saleDate
    ? new Date(invoice.saleDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  return (
    <Modal
      open={open} onClose={onClose}
      hideHead
      size="2xl"
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      <ModalBanner
        title={invoice ? entityName(invoice.toEntityId) : 'Sale Invoice'}
        eyebrow="Sale Invoice"
        right={invoice && (
          <FactPanel>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
                Invoice No
              </div>
              <LotPill>{purchaseNoDisplay(invoice.invoiceNo)}</LotPill>
            </div>
            <VDivider />
            <MiniFact label="Date" value={saleDateLabel} />
          </FactPanel>
        )}
        onClose={onClose}
      />

      {loading && <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Loading…</p>}
      {error && <div className="msg msg-error">{error}</div>}
      {invoice && !loading && (
        <>
          {!isPaid && dd?.isOverdue && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16,
              background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger)',
              borderRadius: 'var(--r-sm)', padding: '6px 12px', fontSize: 12, fontWeight: 700,
            }}>
              {dd.badge}
            </div>
          )}
          {invoice.notes && (
            <p className="form-hint" style={{ marginBottom: 12 }}>Notes: {invoice.notes}</p>
          )}
          <SaleLineItemsMiniTable items={invoice.lineItems ?? []} />
        </>
      )}
    </Modal>
  );
}

// Day/Month/Year, not the raw stored "YYYY-MM-DD" — e.g. "28 Sep 2026".
// Same format/helper as InventoryActivity.tsx's own fmtDateDMY (duplicated
// per this app's per-page-helper convention).
function fmtDateDMY(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// A history entry's `createdAt` (serverTimestamp → Firestore Timestamp, or
// an ISO string, or '' on a synthesized legacy row) as epoch millis; 0 if
// unknown (incl. a just-written entry whose server time hasn't synced yet).
function entryMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isNaN(t) ? 0 : t; }
  const o = v as { toMillis?: () => number; seconds?: number };
  if (typeof o.toMillis === 'function') return o.toMillis();
  if (typeof o.seconds === 'number') return o.seconds * 1000;
  return 0;
}

// ── Item history modal — read-only full lifecycle ledger for one parcel's
// Code (purchase, memo out/in, sale, split out/in, returns) — reads the
// same itemCodes/{codeId}/history ledger and EVENT_LABEL/EVENT_COLOR
// mapping Memos.tsx's own MemoDetailModal uses, duplicated locally per
// this app's per-page-helper convention. Replaces the old SKU-only
// Purchase/Merge ledger (skus/{skuId}/history) that used to back this
// button — that one only covered SKU cost-averaging events and skipped
// every item without a SKU (i.e. every certified stone, which never got
// a History option at all); this one is keyed by Code instead, so any
// item (certified or loose) gets its whole story — Purchased, Sent on
// Memo, Returned, Sold, Split — in one place. ──────────────────────────

const EVENT_LABEL: Record<CodeHistoryEntry['type'], string> = {
  'purchase':  'Purchased',
  'memo-out':  'Sent on Memo',
  'memo-in':   'Returned',
  'sale':      'Sold',
  'split-out': 'Split Out',
  'split-in':  'Split In',
  'return-out': 'Returned to Supplier',
  'return-in':  'Returned by Customer',
  'dummy-credit': 'Cash Credit (Books Adj.)',
  'dummy-debit':  'Cash Debit (Books Adj.)',
  'revaluation':  'Revalued',
};
const EVENT_COLOR: Record<CodeHistoryEntry['type'], string> = {
  'purchase':  'var(--primary)',
  'memo-out':  '#c2410c',
  'memo-in':   'var(--text-2)',
  'sale':      'var(--success)',
  'split-out': 'var(--warning)',
  'split-in':  'var(--primary)',
  'return-out': 'var(--danger)',
  'return-in':  'var(--success)',
  'dummy-credit': 'var(--danger)',
  'dummy-debit':  'var(--success)',
  'revaluation':  'var(--warning)',
};

// Only the latest N rows are ever shown (see displayEntries below) — with
// thousands of events on a long-lived Code this doesn't belong loaded in
// full on screen, same reasoning as InventoryMerge.tsx's own HISTORY_CAP.
// The underlying fetch itself stays uncapped (see the effect below) because
// simulateCostPerCt needs the WHOLE ledger from day one to run correctly —
// only the display slice is capped.
const ITEM_HISTORY_CAP = 50;

// Display-only "what would Cost/ct become" simulation for the Item History
// table's Residual Cost/ct column — explicitly NOT real data. The item's actual
// costUSD/costingUSD never move this way; this just replays the ledger
// client-side to illustrate, next to each Sold/Purchased row, a running
// Cost/ct that absorbs the profit or loss realized on whatever left at each
// step: sell a slice above the running Cost/ct and what's left gets a lower
// Cost/ct; sell below and what's left gets a higher one. A 'purchase'
// CodeHistoryEntry's own price/ct is always the BASE cost (pre-Added-Value,
// matches costUSD not costingUSD — see Purchase.tsx's write of it), so
// `avRatio` (this item's own live costingUSD/costUSD — a ratio the real,
// untouched proportional-shrink system keeps constant at every point in
// time) converts each purchase leg to a costing-equivalent figure before
// folding it into the running pool. split-out/return-out move the pool the
// same direction a sale would; split-in/return-in the same as a purchase
// (already costing-equivalent, no ratio needed — see the Split & Merge cut
// pricing note in CLAUDE.md). memo-out/in and dummy-credit/debit don't move
// carat or cost and are skipped.
function simulateCostPerCt(entries: CodeHistoryEntry[], avRatio: number): Map<string, number> {
  const result = new Map<string, number>();
  let carat = 0;
  let pool  = 0;
  for (const e of entries) {
    const amt = e.amountUSD ?? 0;
    if (e.type === 'purchase') { carat += e.carat; pool += amt * avRatio; }
    else if (e.type === 'split-in' || e.type === 'return-in') { carat += e.carat; pool += amt; }
    else if (e.type === 'sale' || e.type === 'split-out') { carat -= e.carat; pool -= amt; }
    // Back to the supplier at what we paid: take it out at Price (paid + Added Value %).
    else if (e.type === 'return-out') { carat -= e.carat; pool -= amt * avRatio; }
    // A revaluation sets a new figure per ct for everything still held.
    else if (e.type === 'revaluation') { pool = carat * (e.pricePerCt ?? 0); }
    // Memo out / back and other events don't move it: carry the current
    // figure so those rows (and the header) show it unchanged.
    if (carat > 1e-9) result.set(e.id, pool / carat);
  }
  return result;
}

// A 'split-in'/'split-out' CodeHistoryEntry's `notes` field is written as
// "From code {code}" / "To code {code}" (see InventoryMerge.tsx) — pulls
// just the code back out, so the History table can show it as a pill in
// the Counterparty column (these events have no entityId, unlike
// purchase/sale) instead of a bare "—".
function parseLinkedCode(notes?: string): string | undefined {
  const m = notes?.match(/^(?:From|To) code (.+)$/);
  return m?.[1];
}

// For a 'split-in' row — "mixed in from code X" — the user wants to trace
// through to X's OWN original purchase invoice, not just see that a merge
// happened. One hop only (X's current InventoryItem.purchaseId): if X's own
// material was itself assembled from an earlier merge rather than a direct
// purchase, this comes back null rather than chasing the chain further.
async function resolvePurchaseIdForSourceCode(db: ReturnType<typeof getClientDb>, code: string): Promise<string | null> {
  if (!db) return null;
  const codeSnap = await getDoc(doc(db, 'itemCodes', normalizeCode(code)));
  if (!codeSnap.exists()) return null;
  const invItemId = (codeSnap.data() as ItemCode).inventoryItemId;
  if (!invItemId) return null;
  const itemSnap = await getDoc(doc(db, 'inventory', invItemId));
  if (!itemSnap.exists()) return null;
  return (itemSnap.data() as InventoryItem).purchaseId ?? null;
}

// Purchase / supplier return rows carry what we paid; the popup shows them
// as Price/ct (paid plus Added Value %) so the column has one meaning.
const PAID_ROWS = new Set<string>(['purchase', 'return-out']);

function IconNewTab() {
  return <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M7 1.5h3.5V5M10.5 1.5L5.5 6.5M9 7.5v2.5a.5.5 0 01-.5.5h-6.5a.5.5 0 01-.5-.5V3.5a.5.5 0 01.5-.5H5"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

function ItemHistoryModal({ open, onClose, item, entityName, onOpenPurchase, onOpenSale, onMemo, onMemoIn }: {
  open: boolean; onClose: () => void; item: InventoryItem | null; entityName: (id?: string) => string;
  // Opens the Purchase/Sale invoice preview, or the Memo/Memo In modal, ON
  // TOP of this modal — this modal's own `open`/`item` are left untouched,
  // so it stays open underneath (each is just another portaled Modal with
  // the same overlay z-index, stacked by later mount order) rather than
  // being closed to navigate to it. Send On Memo / Memo In used to live in
  // the row's own Actions menu — that column is gone now that a row click
  // opens this modal, so these two buttons moved here instead.
  onOpenPurchase: (purchaseId: string) => void;
  onOpenSale: (saleId: string) => void;
  onMemo: (item: InventoryItem, residualCostPerCt: number) => void;
  onMemoIn: (item: InventoryItem) => void;
}) {
  const settings = useSettings();
  const { user: me } = useAuth();
  const money = (v: number) => fmtMoney(v, settings.currency);
  const [entries, setEntries] = useState<CodeHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !item?.code) { setEntries([]); setLoading(false); return; }
    const db = getClientDb();
    if (!db) return;
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'itemCodes', normalizeCode(item.code), 'history'), orderBy('date', 'asc')),
      // 'estimate': a line saved a moment ago gets its time now, so it sorts
      // last on its day straight away (not first, as with no time yet).
      snap => { setEntries(snap.docs.map(d => ({ id: d.id, ...d.data({ serverTimestamps: 'estimate' }) } as CodeHistoryEntry))); setLoading(false); },
      () => setLoading(false),
    );
    return unsub;
  }, [open, item?.code]);

  // Stock revaluations of this stone: older ones wrote no history line, so
  // they're added here; their saved before-figures also give the Added
  // Value ratio the stone had before a revaluation reset it.
  const [revals, setRevals] = useState<StockRevaluation[]>([]);
  useEffect(() => {
    if (!open || !item?.id) { setRevals([]); return; }
    const db = getClientDb();
    if (!db) return;
    let cancelled = false;
    getDocs(query(collection(db, 'stockRevaluations'), where('inventoryItemId', '==', item.id)))
      .then(snap => { if (!cancelled) setRevals(snap.docs.map(d => ({ id: d.id, ...d.data() } as StockRevaluation))); })
      .catch(() => { if (!cancelled) setRevals([]); });
    return () => { cancelled = true; };
  }, [open, item?.id]);

  // Fallback for a real, pre-existing gap: a 'purchase' CodeHistoryEntry is
  // only ever written for a genuinely NEW line at the time it's purchased
  // (see the comment above Purchase.tsx's edit-branch write of it) — a line
  // that had no Code yet and had one added later via an edit never gets one,
  // even though the item is fully purchased and real. Once the Code ledger
  // has loaded and truly has no 'purchase' row for THIS item, fetch its own
  // purchase invoice once and synthesize one from the invoice's OWN line
  // item (matched by lineItemId) — the originally-purchased carat/cost,
  // immutable regardless of any later partial sale. item.carat/costUSD are
  // deliberately NOT used here: those are the item's CURRENT, live figures —
  // for the still-in-stock remainder of a parcel that's been partially
  // sold, item.carat is what's LEFT (e.g. 40ct), not what was purchased
  // (e.g. 50ct), which showed the wrong number on this exact row.
  const hasOwnPurchaseEntry = entries.some(e => e.type === 'purchase' && e.inventoryItemId === item?.id);
  const [legacyPurchase, setLegacyPurchase] = useState<CodeHistoryEntry | null>(null);
  useEffect(() => {
    if (!open || loading || hasOwnPurchaseEntry || !item?.purchaseId) { setLegacyPurchase(null); return; }
    const db = getClientDb();
    if (!db) return;
    let cancelled = false;
    getDoc(doc(db, 'invoices', item.purchaseId)).then(snap => {
      if (cancelled || !snap.exists()) return;
      const invoice = snap.data() as PurchaseInvoice;
      const li = invoice.lineItems?.find(l => l.id === item.lineItemId);
      const carat     = li?.carat     ?? item.carat;
      const amountUSD = li?.amountUSD ?? item.costUSD;
      setLegacyPurchase({
        id: `legacy-purchase-${item.id}`,
        type: 'purchase', date: invoice.purchaseDate || '',
        carat, entityId: invoice.fromEntityId,
        pricePerCt: carat > 0 ? amountUSD / carat : 0,
        amountUSD, inventoryItemId: item.id,
        purchaseInvoiceId: item.purchaseId, purchaseInvoiceNo: invoice.invoiceNo,
        createdAt: '', createdBy: '',
      });
    }).catch(() => { if (!cancelled) setLegacyPurchase(null); });
    return () => { cancelled = true; };
  }, [open, loading, hasOwnPurchaseEntry, item?.purchaseId, item?.id, item?.lineItemId, item?.carat, item?.costUSD]);

  // Same idea for 'sale' entries — most existing sale entries predate
  // saleInvoiceId/saleInvoiceNo too, so without this they'd show no Invoice
  // No. and wouldn't be clickable. There's no single always-known fallback
  // field for this the way item.purchaseId works for purchases (a sale
  // invoice can carry many different parcels/codes, so nothing on this one
  // InventoryItem points straight at it) — resolved instead by querying
  // that entry's own customer's sales (batched per customer, one query
  // each, not one per row) and matching whichever invoice's lineItems
  // actually contains this entry's inventoryItemId.
  const [legacySales, setLegacySales] = useState<Map<string, { id: string; invoiceNo: string }>>(new Map());
  useEffect(() => {
    if (!open || loading) return;
    const missing = entries.filter(e => e.type === 'sale' && !e.saleInvoiceId && e.entityId);
    if (missing.length === 0) { setLegacySales(new Map()); return; }
    const db = getClientDb();
    if (!db) return;
    let cancelled = false;
    (async () => {
      const customerIds = [...new Set(missing.map(e => e.entityId!))];
      const result = new Map<string, { id: string; invoiceNo: string }>();
      for (const custId of customerIds) {
        const snap = await getDocs(query(collection(db, 'sales'), where('toEntityId', '==', custId)));
        const invoices = snap.docs.map(d => ({ id: d.id, data: d.data() as SaleInvoice }));
        missing.filter(e => e.entityId === custId).forEach(e => {
          const match = invoices.find(inv => inv.data.lineItems?.some(li => li.inventoryItemId === e.inventoryItemId))
            // A partial sale's line points at the source stone, its history line at the sold slice.
            ?? invoices.find(inv => inv.data.saleDate === e.date && !!item?.code
              && inv.data.lineItems?.some(li => li.code === item.code && Math.abs((li.carat ?? 0) - e.carat) < 1e-3));
          if (match) result.set(e.id, { id: match.id, invoiceNo: match.data.invoiceNo });
        });
      }
      if (!cancelled) setLegacySales(result);
    })();
    return () => { cancelled = true; };
  }, [open, loading, entries]);

  // Ascending — required for simulateCostPerCt to replay in true chronological
  // order. The table itself displays newest-first (see displayEntries below);
  // this array stays oldest-first purely for correct running-total math.
  // Same-day entries are ordered by when they were actually recorded
  // (createdAt), so e.g. a purchase and a sale on one day replay in the
  // right sequence instead of whatever order Firestore returned them.
  const allEntries = useMemo(() => {
    const logged = new Set(entries.filter(e => e.type === 'revaluation').map(e => e.revaluationId));
    const oldRevals: CodeHistoryEntry[] = revals.filter(r => !logged.has(r.id)).map(r => ({
      id: `reval_${r.id}`, type: 'revaluation', date: r.date, carat: r.carat ?? 0,
      pricePerCt: r.newCostPerCt, amountUSD: r.newCostUSD, revaluationId: r.id,
      oldPricePerCt: (r.carat ?? 0) > 0 ? (r.oldCostingUSD ?? 0) / (r.carat ?? 1) : undefined,
      inventoryItemId: r.inventoryItemId, notes: r.reason,
      createdAt: r.createdAt, createdBy: r.createdBy,
    }) as CodeHistoryEntry);
    return [...entries, ...oldRevals, ...(legacyPurchase ? [legacyPurchase] : [])]
      .sort((a, b) => a.date.localeCompare(b.date) || entryMillis(a.createdAt) - entryMillis(b.createdAt));
  }, [entries, legacyPurchase, revals]);
  // Added Value ratio (Price ÷ Cost): from before the first revaluation
  // when there is one (a revaluation sets it to 1), else the stone's own.
  const avRatio = useMemo(() => {
    const first = [...revals].sort((a, b) => a.date.localeCompare(b.date))[0];
    if (first && (first.oldCostUSD ?? 0) > 0) return (first.oldCostingUSD ?? 0) / (first.oldCostUSD ?? 1);
    return item && item.costUSD > 0 ? item.costingUSD / item.costUSD : 1;
  }, [revals, item]);
  const simCostPerCt = useMemo(() => simulateCostPerCt(allEntries, avRatio), [allEntries, avRatio]);
  // Newest-first for display — take the most recent ITEM_HISTORY_CAP entries
  // (still the last N of the ascending array) then reverse just this slice.
  // Clicking the Date heading flips it to oldest first (same date + time order).
  const [dateDesc, setDateDesc] = useState(true);
  const latest = allEntries.slice(-ITEM_HISTORY_CAP);
  const displayEntries = dateDesc ? [...latest].reverse() : latest;
  // The simulation's own latest value (as of the most recent event) — this is
  // what the header's Residual Cost/ct fact shows now: what's left of the
  // cost basis per ct after any sale's profit/loss has been absorbed, same
  // figure the last row of the table carries. Falls back to the real base
  // cost/ct when there's no simulated value yet (e.g. no history at all).

  if (!item) return null;

  // Latest figure the replay reached (memo rows carry it, so a memo never moves it).
  let latestSimCostPerCt: number | undefined;
  for (let i = allEntries.length - 1; i >= 0 && latestSimCostPerCt == null; i--) latestSimCostPerCt = simCostPerCt.get(allEntries[i].id);
  const pricePerCt   = perCt(item.costingUSD, item.carat);   // paid + Added Value %
  const residualCostPerCt = latestSimCostPerCt ?? pricePerCt;
  // Same gating the row's old Actions menu used.
  const canMemo   = item.status === 'in-stock' && availableCarat(item) > 1e-9;
  // Stock pages this stone can go to (only the ones the user may open).
  const inStock = item.status === 'in-stock';
  const stockLinks = [
    { key: 'sales',             label: 'Add Sale',      path: '/sales',
      show: inStock && availableCarat(item) > 1e-9 },
    { key: 'stock-revaluation', label: 'Revalue',       path: '/inventory/revaluation', show: inStock },
    { key: 'stock-adjustment',  label: 'Adjust',        path: '/inventory/adjustment',  show: inStock },
    { key: 'inventory-merge',   label: 'Split & Merge', path: '/inventory/merge',
      show: inStock && item.type === 'polished-loose' && availableCarat(item) > 1e-9 },
  ].filter(l => l.show && me && canAccessPage(me, l.key));
  const canMemoIn = item.status === 'on-memo' || (item.status === 'in-stock' && (item.memoCommittedCarat ?? 0) > 1e-9);

  return (
    <Modal
      open={open} onClose={onClose} hideHead size="xl"
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      <ModalBanner
        title={`${item.shape} ${item.color}/${item.clarity}`}
        eyebrow="Item History"
        right={
          <FactPanel>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
                Code
              </div>
              {item.code ? <LotPill>{item.code}</LotPill> : <span style={{ opacity: 0.4 }}>—</span>}
            </div>
            <VDivider />
            <MiniFact label="Cts" value={item.carat.toFixed(2)} />
            <VDivider />
            <MiniFact label="Residual Cost/ct" value={money(residualCostPerCt)} />
            <VDivider />
            <MiniFact label="Price/ct" value={money(pricePerCt)} />
          </FactPanel>
        }
        onClose={onClose}
      />

      {(canMemo || canMemoIn || stockLinks.length > 0) && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          {canMemo && <Button variant="secondary" size="sm" onClick={() => onMemo(item, residualCostPerCt)}>Send On Memo</Button>}
          {canMemoIn && <Button variant="secondary" size="sm" onClick={() => onMemoIn(item)}>Memo In</Button>}
          {stockLinks.length > 0 && (canMemo || canMemoIn) && (
            <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '2px 2px' }} />
          )}
          {/* Other stock pages open in a new tab with this stone already picked. */}
          {stockLinks.map(l => (
            <a key={l.path} className="btn btn-secondary btn-sm" href={`${l.path}?item=${encodeURIComponent(item.id)}`}
              target="_blank" rel="noopener noreferrer" title={`Opens ${l.label} in a new tab`}
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {l.label} <IconNewTab />
            </a>
          ))}
        </div>
      )}

      {!item.code ? (
        <p style={{ color: 'var(--text-3)', fontSize: 13 }}>This item has no Code, so it has no lifecycle ledger.</p>
      ) : loading ? (
        <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Loading…</p>
      ) : displayEntries.length === 0 ? (
        <p style={{ color: 'var(--text-3)', fontSize: 13 }}>No history yet.</p>
      ) : (
        // Own fixed-height scroll region (inline override, not a global
        // .table-wrap change — every other page's use of that class stays
        // untouched) so a long history scrolls in place with the header/
        // facts panel above it staying put, instead of the whole modal body
        // growing past the screen. Sticky <th> keeps the column headers
        // visible while scrolling, same technique as Purchase.tsx's STH.
        <div className="table-wrap table-wrap--compact" style={{ maxHeight: 480, overflowY: 'auto' }}>
          <table style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', top: 0, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => setDateDesc(d => !d)}
                  title={dateDesc ? 'Newest first. Click for oldest first.' : 'Oldest first. Click for newest first.'}>
                  Date <span style={{ fontSize: 10, color: 'var(--primary)' }}>{dateDesc ? '▼' : '▲'}</span>
                </th>
                <th style={{ position: 'sticky', top: 0 }}>Event</th>
                <th style={{ position: 'sticky', top: 0 }}>Invoice No.</th>
                <th style={{ position: 'sticky', top: 0 }}>Counterparty</th>
                <th style={{ position: 'sticky', top: 0, textAlign: 'right' }}>Carat</th>
                <th style={{ position: 'sticky', top: 0, textAlign: 'right' }} title="Purchase and supplier return: what we paid plus Added Value %. Revalued: the new figure. Sale and memo: the price agreed.">Price/ct</th>
                <th
                  style={{ position: 'sticky', top: 0, textAlign: 'right' }}
                  title="Illustrative only. What's left of the cost per ct after each sale's profit or loss is absorbed into the remainder, replayed from this history. Not the booked Cost/ct (see the facts above)."
                >
                  Residual Cost/ct
                </th>
                <th style={{ position: 'sticky', top: 0, textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {displayEntries.map(e => {
                // A 'purchase' row always resolves — via its own
                // purchaseInvoiceId when present, or item.purchaseId as a
                // fallback (always known for the currently-viewed item,
                // regardless of whether this ledger entry predates that
                // field). A 'sale' row uses saleInvoiceId when present, or
                // the legacySales lookup resolved above. A 'split-in' row —
                // material mixed in from another code — resolves on click
                // to THAT code's own original purchase invoice, one hop only.
                const legacySale = e.type === 'sale' ? legacySales.get(e.id) : undefined;
                const saleInvoiceId = e.saleInvoiceId ?? legacySale?.id;
                const invoiceNo = e.type === 'purchase' ? e.purchaseInvoiceNo : e.type === 'sale' ? (e.saleInvoiceNo ?? legacySale?.invoiceNo) : undefined;
                const linkedCode = (e.type === 'split-in' || e.type === 'split-out') ? parseLinkedCode(e.notes) : undefined;
                const clickable = e.type === 'purchase' || (e.type === 'sale' && !!saleInvoiceId) || (e.type === 'split-in' && !!linkedCode);

                async function handleRowClick() {
                  if (e.type === 'purchase') {
                    const pid = e.purchaseInvoiceId ?? item!.purchaseId;
                    if (pid) onOpenPurchase(pid);
                    return;
                  }
                  if (e.type === 'sale' && saleInvoiceId) {
                    onOpenSale(saleInvoiceId);
                    return;
                  }
                  if (e.type === 'split-in' && linkedCode) {
                    const pid = await resolvePurchaseIdForSourceCode(getClientDb(), linkedCode);
                    if (pid) onOpenPurchase(pid);
                  }
                }

                return (
                  <tr
                    key={e.id}
                    onClick={clickable ? handleRowClick : undefined}
                    style={clickable ? { cursor: 'pointer' } : undefined}
                    title={
                      e.type === 'purchase' || (e.type === 'sale' && clickable)
                        ? `Open ${e.type === 'purchase' ? 'Purchase' : 'Sale'} Invoice${invoiceNo ? ` ${invoiceNo}` : ''}`
                        : e.type === 'split-in' && linkedCode
                        ? `Open the purchase invoice for code ${linkedCode}, which this was mixed in from`
                        : undefined
                    }
                  >
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDateDMY(e.date)}</td>
                    <td>
                      <span className="badge" style={{
                        fontSize: 10, fontWeight: 700,
                        background: `color-mix(in srgb, ${EVENT_COLOR[e.type]} 14%, transparent)`,
                        color: EVENT_COLOR[e.type],
                        border: `1px solid color-mix(in srgb, ${EVENT_COLOR[e.type]} 30%, transparent)`,
                      }}>{EVENT_LABEL[e.type]}</span>
                    </td>
                    <td style={{ fontFamily: 'var(--font-num)' }}>
                      {invoiceNo ? <span title={invoiceNo}>{purchaseNoDisplay(invoiceNo)}</span> : <span style={{ opacity: 0.4, fontFamily: 'inherit' }}>—</span>}
                    </td>
                    <td style={{ fontSize: 11 }}>
                      {linkedCode ? <LotPill>{linkedCode}</LotPill>
                        : e.entityId ? entityName(e.entityId)
                        : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{e.carat.toFixed(2)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}
                      title={e.type === 'revaluation' && e.oldPricePerCt != null ? `Was ${money(e.oldPricePerCt)}` : undefined}>
                      {e.pricePerCt != null ? money(PAID_ROWS.has(e.type) ? e.pricePerCt * avRatio : e.pricePerCt) : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--text-3)' }}>
                      {simCostPerCt.has(e.id) ? money(simCostPerCt.get(e.id)!) : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 600 }}>{e.amountUSD != null ? money(e.amountUSD) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function InventoryPage() {
  const settings = useSettings();
  const { user: me } = useAuth();
  const money = (v: number) => fmtMoney(v, settings.currency);
  const [items,    setItems]    = useState<InventoryItem[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading,  setLoading]  = useState(true);
  // See TransactionCategory. 'real' hides books-dummy items (books-only, no
  // physical stock); 'books' hides cash-real items (physical but off the
  // books). Defaults to 'real' — a two-way toggle, not a three-way filter;
  // there's no "All" option here. Filters by exclusion, not inclusion, so a
  // legacy item with no category at all (every item purchased before this
  // feature) passes both views unaffected — no backfill needed.
  const { categoryFilter } = useCategoryFilter();
  const [search, setSearch] = useState('');
  const [page,   setPage]   = useState(1);
  const [sortBy,  setSortBy]  = useState<SortKey>('sku');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [memoItem,   setMemoItem]   = useState<InventoryItem | null>(null);
  const [memoResidual, setMemoResidual] = useState<number | undefined>(undefined);
  const [returnItem, setReturnItem] = useState<InventoryItem | null>(null);
  const [purchasePreview,      setPurchasePreview]      = useState<PurchaseInvoice | null>(null);
  const [purchasePreviewOpen,  setPurchasePreviewOpen]  = useState(false);
  const [purchasePreviewLoad,  setPurchasePreviewLoad]  = useState(false);
  const [purchasePreviewError, setPurchasePreviewError] = useState('');
  const [salePreview,      setSalePreview]      = useState<SaleInvoice | null>(null);
  const [salePreviewOpen,  setSalePreviewOpen]  = useState(false);
  const [salePreviewLoad,  setSalePreviewLoad]  = useState(false);
  const [salePreviewError, setSalePreviewError] = useState('');
  const [historyItem, setHistoryItem] = useState<InventoryItem | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>(loadVisibleColumns);

  useEffect(() => {
    localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  function toggleColumn(key: ColumnKey) {
    setVisibleColumns(v => ({ ...v, [key]: !v[key] }));
  }

  async function openPurchasePreview(purchaseId: string) {
    setPurchasePreviewOpen(true);
    setPurchasePreviewLoad(true);
    setPurchasePreviewError('');
    setPurchasePreview(null);
    const db = getClientDb();
    if (!db) { setPurchasePreviewError('Database not connected.'); setPurchasePreviewLoad(false); return; }
    try {
      const snap = await getDoc(doc(db, 'invoices', purchaseId));
      if (snap.exists()) setPurchasePreview({ id: snap.id, ...snap.data() } as PurchaseInvoice);
      else setPurchasePreviewError('This purchase invoice could not be found.');
    } catch (e) {
      setPurchasePreviewError(e instanceof Error ? e.message : 'Failed to load purchase.');
    }
    setPurchasePreviewLoad(false);
  }

  // A Sold row opens the full sale popup (Items · Payments · Brokers) for
  // users who can open Sales or Receivable; others get the read only preview.
  // (salePopup is created below, after entityName: it reads company names
  // while rendering, so it must come after entityMap exists.)
  async function openSalePreview(saleId: string) {
    if (me && (canAccessPage(me, 'sales') || canAccessPage(me, 'ar'))) {
      if (await salePopup.open(saleId, { tab: 'stones' })) return;
    }
    setSalePreviewOpen(true);
    setSalePreviewLoad(true);
    setSalePreviewError('');
    setSalePreview(null);
    const db = getClientDb();
    if (!db) { setSalePreviewError('Database not connected.'); setSalePreviewLoad(false); return; }
    try {
      const snap = await getDoc(doc(db, 'sales', saleId));
      if (snap.exists()) setSalePreview({ id: snap.id, ...snap.data() } as SaleInvoice);
      else setSalePreviewError('This sale invoice could not be found.');
    } catch (e) {
      setSalePreviewError(e instanceof Error ? e.message : 'Failed to load sale.');
    }
    setSalePreviewLoad(false);
  }

  // ── Firestore listeners ──────────────────────────────────────────────────
  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const u1 = onSnapshot(
      query(collection(db, 'inventory'), orderBy('createdAt', 'desc')),
      snap => { setItems(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem))); setLoading(false); },
      () => setLoading(false),
    );
    const u2 = onSnapshot(
      query(collection(db, 'entities'), orderBy('companyName')),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))),
    );
    return () => { u1(); u2(); };
  }, []);

  const customers = useMemo(() => entities.filter(e => e.types.includes('customer')), [entities]);
  // O(1) lookup instead of entities.find() — matters once a list this reads
  // from (sorting, "print the whole list") is doing it per row instead of
  // just for the current page.
  const entityMap = useMemo(() => new Map(entities.map(e => [e.id, e])), [entities]);
  function entityName(id?: string) { return id ? (entityMap.get(id)?.companyName ?? '—') : '—'; }
  const salePopup = useSalePopup(id => entityName(id));
  // The popups get the stone as it was when clicked; follow the live list
  // instead, so Item History shows the new On Memo / Free Cts (and drops
  // Memo In) right after a memo goes out or comes back.
  const live = (it: InventoryItem | null) => (it ? items.find(i => i.id === it.id) ?? it : null);
  const liveHistoryItem = live(historyItem);
  const liveMemoItem    = live(memoItem);
  const liveReturnItem  = live(returnItem);

  // ── Filtering ─────────────────────────────────────────────────────────────
  const baseVisible = useMemo(() => {
    let list = items;
    // Sold items are fully hidden here — Sales already shows them with the
    // context (customer, price, margin) that actually matters for a sale.
    list = list.filter(i => i.status !== 'sold');
    // 'merged' items (fully consumed by a Split & Merge) are permanently
    // excluded from the main list — that history now lives on the Split &
    // Merge page's own History panel instead of a toggle here.
    list = list.filter(i => i.status !== 'merged');
    // Written-off stock (lost/stolen/damaged beyond value) is gone from the
    // trading list — its cost has already been booked out to the ledger.
    list = list.filter(i => i.status !== 'written-off');
    // A stone sent back whole to its supplier (Purchase Returns) is gone too:
    // 0 ct, 0 cost. The return itself lives on the Purchase Returns page.
    list = list.filter(i => i.status !== 'returned');
    if (categoryFilter === 'real')  list = list.filter(i => i.category !== 'books-dummy');
    if (categoryFilter === 'books') list = list.filter(i => i.category !== 'cash-real');
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(i =>
      (i.certificateNo ?? '').toLowerCase().includes(q) ||
      i.shape.toLowerCase().includes(q) ||
      (i.purchaseNo ?? '').toLowerCase().includes(q) ||
      (i.sku ?? '').toLowerCase().includes(q) ||
      (i.code ?? '').toLowerCase().includes(q)
    );
    return list;
  }, [items, categoryFilter, search]);

  // Excel-style column filters (tick several values per column). Lab also
  // lists "Loose", so it covers the old Certified / Loose switch.
  const filterColumns = useMemo<FilterColumn<InventoryItem>[]>(() => [
    { key: 'code',         label: 'Code',          get: i => i.code },
    { key: 'sku',          label: 'SKU',           get: i => i.sku },
    { key: 'shape',        label: 'Shape',         get: i => i.shape },
    { key: 'color',        label: 'Color',         get: i => i.color },
    { key: 'clarity',      label: 'Clarity',       get: i => i.clarity, sort: clarityCompare },
    { key: 'lab',          label: 'Lab',           get: i => i.type === 'polished-certified' ? (i.lab || 'No lab') : 'Loose' },
    { key: 'size',         label: 'Size',          get: i => i.size },
    { key: 'subSize',      label: 'Sub Size',      get: i => i.subSize },
    { key: 'grading',      label: 'Cut/Pol/Sym',   get: i => [i.cut, i.polish, i.symmetry].some(Boolean) ? [i.cut, i.polish, i.symmetry].map(x => x || '—').join('/') : '' },
    { key: 'fluorescence', label: 'Fluor.',        get: i => i.fluorescence },
    { key: 'status',       label: 'Status',        get: i => STATUS_MAP[i.status]?.label ?? i.status },
    // No header funnel: set by the All / Loose / Certified switch, shown as a chip.
    { key: 'stock',        label: 'Stock',         get: i => i.type === 'polished-certified' ? 'Certified' : 'Loose' },
  ], []);
  const { rows: visible, filters: colFilters } = useColumnFilters(baseVisible, filterColumns);
  const filterable = new Set(filterColumns.map(c => c.key));
  const stockPick  = colFilters.selected.stock?.length === 1 ? colFilters.selected.stock[0] : 'All';

  useEffect(() => setPage(1), [categoryFilter, search, colFilters.stamp]);

  // ── Sorting ───────────────────────────────────────────────────────────────
  function handleSort(key: SortKey) {
    if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('desc'); }
  }
  function compareItems(a: InventoryItem, b: InventoryItem): number {
    switch (sortBy) {
      case 'sku':        return (a.sku ?? '').localeCompare(b.sku ?? '');
      case 'code':        return (a.code ?? '').localeCompare(b.code ?? '');
      case 'shape':       return a.shape.localeCompare(b.shape);
      case 'carat':       return inHandCarat(a) - inHandCarat(b);
      case 'color':       return a.color.localeCompare(b.color);
      case 'clarity':     return a.clarity.localeCompare(b.clarity);
      case 'lab':         return (a.lab ?? '').localeCompare(b.lab ?? '');
      case 'size':        return (a.size ?? '').localeCompare(b.size ?? '');
      case 'subSize':     return (a.subSize ?? '').localeCompare(b.subSize ?? '');
      case 'grading':      return `${a.cut ?? ''} ${a.polish ?? ''} ${a.symmetry ?? ''}`.localeCompare(`${b.cut ?? ''} ${b.polish ?? ''} ${b.symmetry ?? ''}`);
      case 'fluorescence': return (a.fluorescence ?? '').localeCompare(b.fluorescence ?? '');
      case 'measurements': return `${a.measLength ?? ''} ${a.measWidth ?? ''} ${a.measDepth ?? ''}`.localeCompare(`${b.measLength ?? ''} ${b.measWidth ?? ''} ${b.measDepth ?? ''}`);
      case 'costPerCt':   return perCt(a.costUSD, a.carat) - perCt(b.costUSD, b.carat);
      case 'pricePerCt':  return perCt(a.costingUSD, a.carat) - perCt(b.costingUSD, b.carat);
      case 'status':      return STATUS_RANK[a.status] - STATUS_RANK[b.status];
    }
  }
  const sortedVisible = useMemo(() => {
    const arr = [...visible];
    arr.sort((a, b) => sortDir === 'asc' ? compareItems(a, b) : compareItems(b, a));
    return arr;
  }, [visible, sortBy, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(visible.length / PAGE_SIZE);
  const paged = sortedVisible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Footer totals — grand totals of the whole filtered/sorted list (same
  // "whole filtered set, not just the current page" rule CSV export/other
  // tfoot totals in this app already follow), shown both on screen and
  // when printing.
  const footerTotals = useMemo(() => {
    const ct     = sortedVisible.reduce((s, item) => s + (item.carat ?? 0), 0);
    const amount = sortedVisible.reduce((s, item) => s + (item.costingUSD ?? 0), 0);
    return { ct, amount, avgPricePerCt: ct > 0 ? amount / ct : 0 };
  }, [sortedVisible]);

  // ── Export — CSV of every filtered/sorted item (not just the current
  // page), using whichever columns are currently visible. Print bypasses
  // pagination the same way, so the printed/PDF'd list is complete too. ──
  function exportRowFor(item: InventoryItem, key: ColumnKey): string {
    const isCert = item.type === 'polished-certified';
    switch (key) {
      case 'code':          return item.code ?? '';
      case 'sku':           return item.sku ?? '';
      case 'shape':         return item.shape ?? '';
      case 'carat':         return inHandCarat(item).toFixed(2);
      case 'color':         return item.color ?? '';
      case 'clarity':       return item.clarity ?? '';
      case 'lab':           return isCert ? `${item.lab ?? ''} ${item.certificateNo ?? ''}`.trim() : '';
      case 'size':          return !isCert ? (item.size ?? '') : '';
      case 'subSize':       return !isCert ? (item.subSize ?? '') : '';
      case 'grading':       return isCert ? `${gradeAbbr(item.cut)}/${gradeAbbr(item.polish)}/${gradeAbbr(item.symmetry)}` : '';
      case 'fluorescence':  return isCert ? (item.fluorescence ?? '') : '';
      case 'measurements':  return isCert ? `${item.measLength ?? ''}-${item.measWidth ?? ''}x${item.measDepth ?? ''}` : '';
      case 'costPerCt':     return perCt(item.costUSD, item.carat).toFixed(2);
      case 'pricePerCt':    return perCt(item.costingUSD, item.carat).toFixed(2);
      case 'status':        return item.status;
    }
  }
  function handleExportCSV() {
    const cols = COLUMNS.filter(c => visibleColumns[c.key]);
    downloadCSV(
      'inventory',
      cols.map(c => c.label),
      sortedVisible.map(item => cols.map(c => exportRowFor(item, c.key))),
    );
  }
  // Waits for two animation frames (not a fixed setTimeout) before calling
  // print() — a guessed delay is either wasted time on a short list or,
  // worse, fires before a long list has actually finished rendering; two
  // rAFs guarantee the browser has painted the just-swapped-in full list
  // first, which is what actually fixes "Print List feels slow to open."
  const [printAll, setPrintAll] = useState(false);
  useEffect(() => {
    if (!printAll) return;
    let cancelled = false;
    const after = () => setPrintAll(false);
    window.addEventListener('afterprint', after);
    requestAnimationFrame(() => {
      if (cancelled) return;
      requestAnimationFrame(() => { if (!cancelled) window.print(); });
    });
    return () => { cancelled = true; window.removeEventListener('afterprint', after); };
  }, [printAll]);
  const displayedItems = printAll ? sortedVisible : paged;
  // The table box ends just above the screen's bottom edge (page numbers under it).
  const stockWrapRef = useRef<HTMLDivElement>(null);
  useFitHeight(stockWrapRef, 56, 280, [displayedItems.length > 0, loading]);
  // Code and SKU stay in place while the other columns scroll sideways
  // (only Code on a phone).
  const stockTableRef = useRef<HTMLTableElement>(null);
  useFrozenColumns(stockTableRef, (visibleColumns.code ? 1 : 0) + (visibleColumns.sku ? 1 : 0),
    visibleColumns.code ? 1 : 0, [visibleColumns, displayedItems.length]);

  const hasActiveFilters = !!search || colFilters.active.length > 0;

  // Skeleton column count always matches the real table's visible columns,
  // so loading → loaded never "jumps" to a different number of columns.
  const skeletonWidths = COLUMNS.filter(c => visibleColumns[c.key]).map(c => c.skeletonWidth);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <AppLayout
      title="Inventory" subtitle="Track your diamond stock through memo and sale"
     toolbar={<>
          {/* Search */}
          <div style={{ width: 220, flexShrink: 0, position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none', display: 'flex' }}>
              <IconSearch />
            </span>
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Code, Cert, shape, purchase…"
              style={{
                width: '100%', paddingLeft: 32, paddingRight: search ? 30 : 10,
                height: 32, fontSize: 12.5, border: '1.5px solid var(--border)',
                borderRadius: 'var(--r-sm)', background: 'var(--surface-2)',
                color: 'var(--text-1)', outline: 'none', boxSizing: 'border-box',
                transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
              }}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'var(--surface)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--surface-2)'; }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{
                position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
                background: 'var(--border)', border: 'none', cursor: 'pointer',
                width: 16, height: 16, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-2)', fontSize: 10, padding: 0,
              }} title="Clear search">×</button>
            )}
          </div>

          {hasActiveFilters && (
            <button type="button" className="tb-clear" onClick={() => { setSearch(''); colFilters.clear(); }}>
              Clear
            </button>
          )}
      </>}
      headerActions={<>
          <div className="tb-seg" role="tablist" aria-label="Stock">
            {['All', 'Loose', 'Certified'].map(k => (
              <button key={k} type="button" role="tab" aria-selected={stockPick === k}
                className={stockPick === k ? 'on' : undefined}
                onClick={() => colFilters.set('stock', k === 'All' ? null : [k])}>{k}</button>
            ))}
          </div>
          {/* Right: tools as one joined group, then the main action last. */}
          <div className="tb-group">
            <Dropdown
              align="right"
              closeOnInnerClick={false}
              trigger={() => (
                <button type="button" className="tb-group-btn" title="Columns" aria-label="Columns"><IconColumns /></button>
              )}
            >
              <div className="dropdown-group-label">Show Columns</div>
              {COLUMNS.map(c => (
                <label key={c.key} className="dropdown-item" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={visibleColumns[c.key]}
                    onChange={() => toggleColumn(c.key)}
                    style={{ accentColor: 'var(--primary)' }}
                  />
                  {c.label}
                </label>
              ))}
              <div className="dropdown-divider" />
              <button type="button" className="dropdown-item" onClick={() => setVisibleColumns({ ...DEFAULT_VISIBLE })}>
                Select All
              </button>
            </Dropdown>
            <Dropdown
              align="right"
              trigger={() => (
                <button type="button" className="tb-group-btn" title="Export" aria-label="Export"><IconExport /></button>
              )}
            >
              <button type="button" className="dropdown-item" onClick={handleExportCSV}>Export CSV</button>
              <button type="button" className="dropdown-item" onClick={() => setPrintAll(true)}>Print List</button>
            </Dropdown>
          </div>
      </>}>



      {/* ── Summary strip + Column customizer — one row, Columns pinned right ──
          Stats reserve their row's height via `visibility` (not conditional
          render) even while loading/empty, so the Columns trigger never
          jumps position — same technique as Sales.tsx's summary strip. */}
      {(() => {
        // In hand only: carats out on memo are counted separately (On Memo).
        const totCt  = visible.reduce((s, i) => s + inHandCarat(i), 0);
        const memoTotCt  = visible.reduce((s, i) => s + memoCt(i), 0);
        const memoTotAmt = visible.reduce((s, i) => s + (i.provenance === 'memo-in' ? 0 : (i.costUSD ?? 0) - inHandCost(i)), 0);
        // Consigned (memo-in) stock is held, not owned — excluded from the
        // value total (it's carried on the Balance Sheet against an equal
        // consignment liability, not in the business's own stock value).
        const totCost  = visible.reduce((s, i) => s + (i.provenance === 'memo-in' ? 0 : inHandCost(i)), 0);
        const totPrice = visible.reduce((s, i) => s + (i.provenance === 'memo-in' ? 0 : inHandCosting(i)), 0);
        const showStats = !loading && visible.length > 0;
        const stats = [
          { label: 'Items',       val: String(visible.length) },
          { label: 'Total ct',    val: `${totCt.toFixed(2)} ct` },
          { label: 'Total Cost',  val: money(totCost) },
          { label: 'Total Price', val: money(totPrice) },
          ...(memoTotCt > 1e-9 ? [{ label: 'On Memo', val: `${memoTotCt.toFixed(2)} ct · ${money(memoTotAmt)} cost` }] : []),
        ];
        return (
          <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 8, rowGap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: '1 1 auto', visibility: showStats ? 'visible' : 'hidden' }}>
              {stats.map(({ label, val }) => (
                <div key={label} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 'var(--r-sm)', padding: '6px 12px',
                  boxShadow: 'var(--shadow-xs)', fontSize: 12,
                }}>
                  <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>{label}</span>
                  <span style={{ width: 1, height: 12, background: 'var(--border)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-1)', fontWeight: 700, fontFamily: 'var(--font-num)' }}>{val}</span>
                </div>
              ))}
            </div>
            <ColumnFilterChips filters={colFilters} inline />
          </div>
        );
      })()}

      {/* Table */}
      {loading ? (
        <div className="table-wrap">
          <table>
            <thead><tr>{skeletonWidths.map((w, i) => (
              <th key={i}><div className="skeleton" style={{ height: 11, width: w, borderRadius: 4 }} /></th>
            ))}</tr></thead>
            <tbody>{[...Array(5)].map((_, i) => (
              <tr key={i} style={{ opacity: 1 - i * 0.16 }}>
                {skeletonWidths.map((w, j) => (
                  <td key={j}><div className="skeleton" style={{ height: 13, borderRadius: 4, width: `${w}%` }} /></td>
                ))}
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : visible.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon" style={{ display: 'flex', justifyContent: 'center' }}><IconEmptyDiamond /></div>
            <div className="empty-state-title">
              {hasActiveFilters ? 'No items match your filter' : 'No inventory yet'}
            </div>
            <div className="empty-state-sub">
              {hasActiveFilters
                ? 'Try adjusting the search or filters above.'
                : 'Inventory is created automatically when you save a purchase invoice.'}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div ref={stockWrapRef} className="table-wrap table-fit table-fit-stats table-freeze-cols">
            <table ref={stockTableRef}>
              <thead>
                <tr>
                  {COLUMNS.filter(c => visibleColumns[c.key]).map(c => (
                    <SortableTh key={c.key} label={c.label} sortKey={c.sortKey} activeKey={sortBy} dir={sortDir} onSort={handleSort}
                      minWidth={c.key === 'size' ? 96 : undefined}
                      width={c.key === 'code' ? 104 : c.key === 'sku' ? 140 : undefined}
                      filter={filterable.has(c.key) ? <ColumnFilterButton filters={colFilters} colKey={c.key} label={c.label} /> : undefined} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedItems.map(item => {
                  const isCert = item.type === 'polished-certified';
                  const isMerged = item.status === 'merged';
                  return (
                    <tr
                      key={item.id}
                      onClick={item.code ? () => setHistoryItem(item) : undefined}
                      style={{ ...(isMerged ? { opacity: 0.55 } : {}), cursor: item.code ? 'pointer' : undefined }}
                    >
                      {visibleColumns.code && (
                        <td>
                          {item.code ? <LotPill>{item.code}</LotPill> : <span style={{ fontSize: 12, color: 'var(--text-2)', opacity: 0.4 }}>—</span>}
                        </td>
                      )}
                      {visibleColumns.sku && (
                        <td style={{ maxWidth: 140 }}>
                          {item.sku ? (
                            <span title={item.sku} style={{
                              display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle',
                              background: 'var(--primary-light)', color: 'var(--primary)',
                              fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
                              padding: '3px 8px', borderRadius: 6, letterSpacing: '0.02em',
                            }}>
                              {item.sku}
                            </span>
                          ) : <span style={{ fontSize: 12, color: 'var(--text-2)', opacity: 0.4 }}>—</span>}
                        </td>
                      )}
                      {visibleColumns.shape && <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{item.shape}</td>}
                      {visibleColumns.carat && (
                        // Only the Dummy/Books Adjustment code can go below
                        // zero (sold ahead of future Cash/Real credits).
                        <td style={{ fontSize: 12, color: item.carat < 0 ? 'var(--danger)' : 'var(--text-2)', fontFamily: 'var(--font-num)' }}
                          title={item.carat < 0 ? 'Below zero. Future Cash/Real sales fill this back'
                            : memoCt(item) > 1e-9 ? `${item.carat.toFixed(2)} ct owned: ${inHandCarat(item).toFixed(2)} in hand, ${memoCt(item).toFixed(2)} on memo` : undefined}>
                          {inHandCarat(item).toFixed(2)}
                          {memoCt(item) > 1e-9 && (
                            <div style={{ fontSize: 10.5, fontWeight: 600, color: '#c2410c', marginTop: 2, whiteSpace: 'nowrap' }}>
                              +{memoCt(item).toFixed(2)} on memo
                            </div>
                          )}
                        </td>
                      )}
                      {visibleColumns.color && <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{item.color}</td>}
                      {visibleColumns.clarity && <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{item.clarity}</td>}
                      {visibleColumns.lab && (
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                          {isCert
                            ? <>{item.lab} · <CertNo lab={item.lab} certificateNo={item.certificateNo} style={{ fontFamily: 'var(--font-num)' }} /></>
                            : <span style={{ opacity: 0.4 }}>—</span>}
                        </td>
                      )}
                      {visibleColumns.size && (
                        <td style={{ fontSize: 12, color: 'var(--text-2)', minWidth: 96, whiteSpace: 'nowrap' }}>
                          {!isCert
                            ? (item.size || <span style={{ opacity: 0.4 }}>—</span>)
                            : <span style={{ opacity: 0.4 }}>—</span>}
                        </td>
                      )}
                      {visibleColumns.subSize && (
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                          {!isCert
                            ? (item.subSize || <span style={{ opacity: 0.4 }}>—</span>)
                            : <span style={{ opacity: 0.4 }}>—</span>}
                        </td>
                      )}
                      {visibleColumns.grading && (
                        <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>
                          {isCert && (item.cut || item.polish || item.symmetry)
                            ? `${gradeAbbr(item.cut)}/${gradeAbbr(item.polish)}/${gradeAbbr(item.symmetry)}`
                            : <span style={{ opacity: 0.4, fontFamily: 'inherit' }}>—</span>}
                        </td>
                      )}
                      {visibleColumns.fluorescence && (
                        <td style={{ fontSize: 12, color: 'var(--text-2)' }}>
                          {isCert && item.fluorescence ? item.fluorescence : <span style={{ opacity: 0.4 }}>—</span>}
                        </td>
                      )}
                      {visibleColumns.measurements && (
                        <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>
                          {isCert && (item.measLength || item.measWidth || item.measDepth)
                            ? `${item.measLength || '—'}-${item.measWidth || '—'}x${item.measDepth || '—'}`
                            : <span style={{ opacity: 0.4, fontFamily: 'inherit' }}>—</span>}
                        </td>
                      )}
                      {visibleColumns.costPerCt && (
                        <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>
                          {money(perCt(item.costUSD, item.carat))}
                        </td>
                      )}
                      {visibleColumns.pricePerCt && (
                        <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>
                          {money(perCt(item.costingUSD, item.carat))}
                        </td>
                      )}
                      {visibleColumns.status && (
                        <td>
                          {item.provenance === 'memo-in'
                            ? <span className="badge" style={{ background: '#eef2ff', color: '#4338ca', border: '1px solid #a5b4fc', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>Consigned</span>
                            : <StatusBadge status={item.status} />}
                          {/* Legacy on-memo doc — old single-memo fields */}
                          {item.status === 'on-memo' && item.memoToEntityId && (
                            <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>{entityName(item.memoToEntityId)}</div>
                          )}
                          {/* Current model — item stays 'in-stock' with part of its carat held */}
                          {item.status === 'sold' && item.soldToEntityId && (
                            <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>{entityName(item.soldToEntityId)}</div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {printAll && sortedVisible.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={COLUMNS.filter(c => visibleColumns[c.key]).length}>
                      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12 }}>
                        <span style={{ color: 'var(--text-2)' }}>
                          Total ct <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{footerTotals.ct.toFixed(2)}</strong>
                        </span>
                        <span style={{ color: 'var(--text-2)' }}>
                          Total Amount <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{money(footerTotals.amount)}</strong>
                        </span>
                        <span style={{ color: 'var(--text-2)' }}>
                          Avg. Price/ct <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{money(footerTotals.avgPricePerCt)}</strong>
                        </span>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="no-print">
            <Pagination current={page} total={totalPages} totalItems={visible.length} pageSize={PAGE_SIZE} onChange={setPage} />
          </div>
        </>
      )}

      <MemoModal open={!!memoItem} onClose={() => setMemoItem(null)} item={liveMemoItem} customers={customers} residualCostPerCt={memoResidual} />
      <ReturnModal open={!!returnItem} onClose={() => setReturnItem(null)} item={liveReturnItem} entities={entities} />
      <PurchasePreviewModal
        open={purchasePreviewOpen}
        onClose={() => setPurchasePreviewOpen(false)}
        invoice={purchasePreview}
        loading={purchasePreviewLoad}
        error={purchasePreviewError}
        entityName={entityName}
      />
      <SaleInvoicePreviewModal
        open={salePreviewOpen}
        onClose={() => setSalePreviewOpen(false)}
        invoice={salePreview}
        loading={salePreviewLoad}
        error={salePreviewError}
        entityName={entityName}
      />
      <ItemHistoryModal
        open={!!historyItem}
        onClose={() => setHistoryItem(null)}
        item={liveHistoryItem}
        entityName={entityName}
        onOpenPurchase={openPurchasePreview}
        onOpenSale={openSalePreview}
        onMemo={(it, residual) => { setMemoResidual(residual); setMemoItem(it); }}
        onMemoIn={setReturnItem}
      />
      {salePopup.popup}
    </AppLayout>
  );
}
